import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Minus, Plus } from "lucide-react";
import { useCart } from "@/contexts/CartContext";
import { useLang } from "@/contexts/LanguageContext";
import { formatPrice, createCheckoutSession } from "@/lib/api";
import Reveal from "@/components/Reveal";
import { toast } from "sonner";

export default function Cart() {
  const { items, updateQty, removeItem, subtotal } = useCart();
  const { lang, t } = useLang();
  const [processing, setProcessing] = useState(false);

  const handleCheckout = async () => {
    if (items.length === 0) return;
    setProcessing(true);
    try {
      const payload = {
        items: items.map((it) => ({
          product_id: it.product_id,
          quantity: it.quantity,
          size: it.size,
          color: it.color,
        })),
        origin_url: window.location.origin,
        locale: lang,
      };
      const res = await createCheckoutSession(payload);
      window.location.href = res.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
      setProcessing(false);
    }
  };

  return (
    <div data-testid="cart-page" className="bg-maly-cream pt-32 lg:pt-40 pb-24 min-h-screen">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <Reveal>
          <div className="font-body text-[11px] tracking-[0.3em] uppercase text-maly-navy/60 mb-6">{t.cart.overline}</div>
        </Reveal>
        <Reveal delay={100}>
          <h1 className="font-display text-5xl lg:text-7xl font-light tracking-[-0.02em] leading-[1.02]">{t.cart.headline}</h1>
        </Reveal>

        {items.length === 0 ? (
          <Reveal delay={200} className="mt-20 text-center py-24 border-t border-maly-navy/10">
            <div className="font-display text-3xl mb-6">{t.common.emptyCart}</div>
            <p className="font-body text-sm opacity-60 mb-8">{t.common.emptyCartCta}</p>
            <Link to="/shop" data-testid="cart-empty-shop" className="btn-primary">
              {t.nav.shop}
            </Link>
          </Reveal>
        ) : (
          <div className="mt-16 grid lg:grid-cols-12 gap-12 lg:gap-20">
            <div className="lg:col-span-8">
              <ul className="divide-y divide-maly-navy/10">
                {items.map((it) => (
                  <li key={it.key} data-testid={`cart-page-item-${it.product_id}`} className="grid grid-cols-12 gap-4 lg:gap-8 py-8">
                    <Link to={`/shop/${it.slug}`} className="col-span-3 aspect-[3/4] bg-maly-creamDeep img-zoom">
                      <img src={it.image} alt="" className="w-full h-full object-cover" />
                    </Link>
                    <div className="col-span-9 flex flex-col justify-between">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <Link to={`/shop/${it.slug}`} className="font-display text-2xl lg:text-3xl font-light">
                            {it.name?.[lang] || it.name?.en}
                          </Link>
                          <div className="font-body text-xs opacity-60 mt-2 tracking-wider">
                            {[it.size, it.color].filter(Boolean).join(" · ")}
                          </div>
                        </div>
                        <div className="font-display text-xl">{formatPrice(it.price * it.quantity)}</div>
                      </div>
                      <div className="flex items-center justify-between mt-4">
                        <div className="flex items-center border border-maly-navy/20">
                          <button data-testid={`cart-page-down-${it.product_id}`} onClick={() => updateQty(it.key, it.quantity - 1)} className="p-2"><Minus size={12} /></button>
                          <span className="px-3 font-body text-sm">{it.quantity}</span>
                          <button data-testid={`cart-page-up-${it.product_id}`} onClick={() => updateQty(it.key, it.quantity + 1)} className="p-2"><Plus size={12} /></button>
                        </div>
                        <button data-testid={`cart-page-remove-${it.product_id}`} onClick={() => removeItem(it.key)} className="font-body text-[10px] tracking-[0.25em] uppercase opacity-60 hover:opacity-100">
                          {t.common.remove}
                        </button>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className="lg:col-span-4">
              <div className="sticky top-32 bg-white border border-maly-navy/10 p-8">
                <h2 className="font-display text-2xl mb-8">{t.common.total}</h2>
                <div className="flex items-center justify-between font-body text-sm pb-5 border-b border-maly-navy/10">
                  <span className="opacity-70">{t.common.subtotal}</span>
                  <span>{formatPrice(subtotal)}</span>
                </div>
                <div className="flex items-center justify-between font-body text-sm py-5 border-b border-maly-navy/10">
                  <span className="opacity-70">{t.common.shippingNote}</span>
                  <span>—</span>
                </div>
                <div className="flex items-center justify-between font-display text-2xl pt-6">
                  <span>{t.common.total}</span>
                  <span data-testid="cart-page-subtotal">{formatPrice(subtotal)}</span>
                </div>

                <button
                  data-testid="cart-page-checkout"
                  onClick={handleCheckout}
                  disabled={processing}
                  className="btn-primary w-full mt-8 disabled:opacity-50"
                >
                  {processing ? t.common.processing : t.common.proceedToCheckout} <ArrowRight size={14} />
                </button>
                <Link to="/shop" className="block text-center font-body text-[11px] tracking-[0.25em] uppercase opacity-70 hover:opacity-100 mt-4">
                  {t.common.continueShopping}
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
